import { useEffect, useRef } from 'react';
import { useChannelStateContext } from 'stream-chat-react';

import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { setStatus } from 'src/store/aiAssistantStatus/slice';

const TAKING_LONGER_TIMEOUT = 15000; // 15 seconds
const ERROR_TIMEOUT = 30000; // 30 seconds

export interface AIAssistantStatusData {
  status: string;
  metadata?: {
    tool?: string;
    query?: string;
    errorMessage?: string;
  };
}

export const useAIAssistantStatus = (): AIAssistantStatusData | null => {
  const dispatch = useAppDispatch();
  const { channel } = useChannelStateContext();
  const channelId = channel?.cid;

  const status = useAppSelector((state) =>
    channelId ? (state.aiAssistantStatus.statuses[channelId] ?? null) : null,
  );

  const takingLongerTimeoutRef = useRef<NodeJS.Timeout>();
  const errorTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Always clear existing timeouts first to prevent leaks
    if (takingLongerTimeoutRef.current) {
      clearTimeout(takingLongerTimeoutRef.current);
      takingLongerTimeoutRef.current = undefined;
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = undefined;
    }

    // If backend sends an error status or no status, don't set any new timeouts
    if (!status || !channelId || status.status === 'error') {
      return;
    }

    // Capture current values to avoid race conditions
    const currentChannelId = channelId;
    const currentMetadata = status.metadata;

    // Only set timeouts if we have an active status that's not already an error
    if (status.status !== 'taking_longer') {
      // Set "taking longer" timeout
      takingLongerTimeoutRef.current = setTimeout(() => {
        dispatch(
          setStatus({
            channelId: currentChannelId,
            status: 'taking_longer',
            metadata: currentMetadata,
          }),
        );
      }, TAKING_LONGER_TIMEOUT);

      // Set error timeout
      errorTimeoutRef.current = setTimeout(() => {
        dispatch(
          setStatus({
            channelId: currentChannelId,
            status: 'error',
            metadata: {
              ...currentMetadata,
              errorMessage: 'Request timed out. Please try again.',
            },
          }),
        );
      }, ERROR_TIMEOUT);
    } else {
      // We're in taking_longer state, set a shorter error timeout
      errorTimeoutRef.current = setTimeout(() => {
        dispatch(
          setStatus({
            channelId: currentChannelId,
            status: 'error',
            metadata: {
              ...currentMetadata,
              errorMessage: 'Request timed out. Please try again.',
            },
          }),
        );
      }, ERROR_TIMEOUT - TAKING_LONGER_TIMEOUT); // Remaining time
    }

    // Cleanup on unmount or dependency change
    return () => {
      if (takingLongerTimeoutRef.current) {
        clearTimeout(takingLongerTimeoutRef.current);
        takingLongerTimeoutRef.current = undefined;
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = undefined;
      }
    };
  }, [status, channelId, dispatch]);

  return status;
};
